import React, { useEffect, useRef, useState } from 'react';
import { personLabel } from '@stepflow/ui-shared/utils/personLabel.js';
import { MoreVertical, Pencil, Copy, Trash2, ThumbsUp, ThumbsDown, HelpCircle, MessageSquare, ChevronRight, ChevronDown, Circle, BookmarkPlus } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import PersonAvatar from '@stepflow/ui-shared/components/PersonAvatar.jsx';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import { keepLineBreaks } from '@stepflow/ui-shared/utils/textFormat.js';
import useSmoothText from '@stepflow/ui-shared/hooks/useSmoothText.js';
import { contextModelLabel } from './ContextModelPicker.jsx';
import { WebProgress, SourceCards, linkCitations } from './WebResearch.jsx';
import { formatMessageTime, formatMessageTimeFull, formatClockTime } from '@stepflow/ui-shared/utils/formatDate.js';

/** LONG MESSAGE (MODES.md §3d) — Slack-style: a message taller than ``LONG_MESSAGE_PX`` shows that much with a fade and
 *  "Show more"; "Show less" folds it back. Measured (ResizeObserver), so it follows live edits / streaming and the
 *  user's font size; a short message is untouched. Code panels inside collapse on their own (CONTEXT-DOCS §P4). */
export const LONG_MESSAGE_PX = 320;
const _fade = 'linear-gradient(to bottom, #000 calc(100% - 4rem), transparent)';
const FADE = { maskImage: _fade, WebkitMaskImage: _fade };
export function LongMessage({ id, children, maxPx = LONG_MESSAGE_PX }) {
  const ref = useRef(null);
  const [tall, setTall] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setTall(el.scrollHeight > maxPx + 24);      // a little slack: never clamp off one line
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxPx]);
  const clamped = tall && !open;
  return (
    <div data-testid={`context-msg-body-${id}`} data-clamped={clamped ? 'true' : 'false'}>
      {/* the fade is a MASK on the text itself, so it works on any row shade (hover grey, @mention amber) */}
      <div ref={ref} className="overflow-hidden" style={clamped ? { maxHeight: `${maxPx}px`, ...FADE } : undefined}>
        {children}
      </div>
      {tall ? (
        <button type="button" data-testid={`context-msg-more-${id}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="mt-0.5 text-[0.8125rem] font-medium text-indigo-700 hover:text-indigo-900 hover:underline">
          {open ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

/** Who wrote it — ONE rule (L152): a real full name, else the email. A stored "Amarpani" (re-cased from the email by
 *  older code) reads as the email, matching every other message. */
export function authorLabel(m, personOf, fallback = 'Teammate') {
  const known = (personOf && m && m.authorSub && personOf(m.authorSub)) || {};
  const email = (m && m.authorEmail) || known.email || (String((m && m.authorName) || '').includes('@') ? m.authorName : '');
  return personLabel({ name: m && m.authorName, email }, '') || personLabel(known, '') || fallback;
}


/**
 * CONTEXT MODE (MODES.md §3) — the room discussion lives IN the room's activity stream, like Slack with agents
 * as members (owner 2026-10-03: "anything is an activity"). This file holds the stream pieces: one message row,
 * the start hint for an empty Context room, and the slim Build-context bar above the composer.
 */
// the day + time (a bare "11:09 AM" is ambiguous across days) — unless the day header above already names the day
// (``timeOnly``: Today / Yesterday / a weekday) → just the clock. Full date on hover either way.
const _time = (at, timeOnly) => (timeOnly ? formatClockTime(at) : formatMessageTime(at));


// SLACK MESSAGE METRICS (owner 2026-10-11 — "copy Slack's UX"), all REM/EM so they follow the user's root font /
// zoom (CSS px are already device-independent on high-DPI screens; rem additionally honours the reader's scale):
// 15px body / 1.4667 line-height, 15px bold sender, 12px time, 36px avatar, a full-width hover shade, and a
// floating action bar (32px buttons, 18px icons) that pops at the top-right of the hovered message.
const MSG_TEXT = 'text-[0.9375rem] leading-[1.46668]';
const BAR_BTN = 'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400';
const BAR_ICON = 'h-[1.125rem] w-[1.125rem]';
const MENU_ITEM = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[0.875rem]';

// TEAM SIGNALS (MODES.md §3a) — labelled, not emoji: each FEEDS the context build (agreed → may be decided; disputed
// / unclear → Open questions + Gaps) and, on an agent's answer, the quality watch.
export const MSG_TEXT_CLASS = MSG_TEXT;
export const SIGNALS = [
  { key: 'agree', label: 'Agree', Icon: ThumbsUp, on: 'bg-emerald-50 text-emerald-700 ring-emerald-300', verb: ['agrees', 'agree'] },
  { key: 'disagree', label: 'Disagree', Icon: ThumbsDown, on: 'bg-rose-50 text-rose-700 ring-rose-300', verb: ['disagrees', 'disagree'] },
  { key: 'unclear', label: 'Unclear', Icon: HelpCircle, on: 'bg-amber-50 text-amber-800 ring-amber-300', verb: ['finds it unclear', 'find it unclear'] },
];
const _mine = (m, me, key) => !!me && ((m.signals || {})[key] || []).includes(me);

/** "Pani, Raj and 2 others agree" / "Ana agrees" / "You agree" — who signalled (you first). ``verb`` =
 *  [singular, plural]. Pure. */
export function signalTitle(subs, me, nameOf, verb) {
  const [one, many] = Array.isArray(verb) ? verb : [verb, verb];
  const list = [...(subs || [])].sort((a, b) => (a === me ? -1 : b === me ? 1 : 0))
    .map((s) => (s === me ? 'You' : (nameOf && nameOf(s)) || 'Someone'));
  if (!list.length) return '';
  const head = list.length <= 3 ? list : [...list.slice(0, 2), `${list.length - 2} others`];
  const who = head.length === 1 ? head[0] : `${head.slice(0, -1).join(', ')} and ${head[head.length - 1]}`;
  return `${who} ${list.length === 1 && list[0] !== 'You' ? one : many}`;
}

/** The pills under a message (Slack's reaction row): count per signal, highlighted when it's mine; click toggles. */
function SignalPills({ m, me, nameOf, onSignal }) {
  const shown = SIGNALS.filter((s) => ((m.signals || {})[s.key] || []).length > 0);
  if (!shown.length) return null;
  return (
    <div data-testid={`context-msg-signals-${m.id}`} className="mt-1 flex flex-wrap gap-1">
      {shown.map(({ key, label, Icon, on, verb }) => {
        const subs = (m.signals || {})[key] || [];
        const mine = _mine(m, me, key);
        return (
          <button key={key} type="button" data-testid={`context-msg-signal-pill-${key}-${m.id}`} aria-pressed={mine}
            disabled={typeof onSignal !== 'function'} onClick={() => onSignal && onSignal(m, key, !mine)}
            title={`${signalTitle(subs, me, nameOf, verb)} — ${label}`}
            className={`inline-flex h-6 items-center gap-1 rounded-full px-2 text-[0.75rem] font-medium ring-1 transition-colors ${
              mine ? on : 'bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100'}`}>
            <Icon className="h-3.5 w-3.5" aria-hidden />{subs.length}
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The hover ACTION BAR (Slack): pops at the message's top-right on hover / keyboard focus — the three SIGNALS
 *  (Agree · Disagree · Unclear), ✏ Edit (your own) and ⋮ More (Copy text · Edit message · Delete message).
 *  Stays up while its menu is open. */
const KEEP_LABEL = { document: 'Keep as a document', procedure: 'Keep as a procedure', qa: 'Keep as a trained answer' };

function MessageActions({ m, me, editable, deletable, onStartEdit, onDelete, onMenuOpen, onSignal, onReplyInThread,
  onKeep = null, keepKinds = [] }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const toggle = (v) => { setOpen(v); onMenuOpen(v); };
  const copy = () => { try { navigator.clipboard && navigator.clipboard.writeText(m.text || ''); } catch (e) { /* no clipboard */ } };
  return (
    <div data-testid={`context-msg-actions-${m.id}`} role="toolbar" aria-label="Message actions"
      className={`absolute -top-4 right-3 z-10 items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm ${open ? 'flex' : 'hidden group-hover:flex group-focus-within:flex'}`}>
      {typeof onSignal === 'function' ? (<>
        {SIGNALS.map(({ key, label, Icon }) => {
          const mine = _mine(m, me, key);
          return (
            <button key={key} type="button" data-testid={`context-msg-signal-${key}-${m.id}`} aria-pressed={mine}
              aria-label={mine ? `Remove ${label}` : label} title={mine ? `Remove ${label}` : label}
              onClick={() => onSignal(m, key, !mine)} className={`${BAR_BTN} ${mine ? 'text-indigo-700' : ''}`}>
              <Icon className={BAR_ICON} aria-hidden />
            </button>
          );
        })}
        <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden />
      </>) : null}
      {typeof onReplyInThread === 'function' ? (
        <button type="button" data-testid={`context-msg-reply-${m.id}`} aria-label="Reply in thread" title="Reply in thread"
          onClick={() => onReplyInThread(m)} className={BAR_BTN}>
          <MessageSquare className={BAR_ICON} aria-hidden />
        </button>
      ) : null}
      {editable ? (
        <button type="button" data-testid={`context-msg-quickedit-${m.id}`} aria-label="Edit message" title="Edit message"
          onClick={onStartEdit} className={BAR_BTN}>
          <Pencil className={BAR_ICON} aria-hidden />
        </button>
      ) : null}
      <button type="button" ref={ref} data-testid={`context-msg-menu-${m.id}`} aria-label="More actions" title="More actions"
        aria-haspopup="menu" aria-expanded={open} onClick={() => toggle(!open)} className={`${BAR_BTN} ${open ? 'bg-gray-100 text-gray-900' : ''}`}>
        <MoreVertical className={BAR_ICON} aria-hidden />
      </button>
      <PortalMenu anchorRef={ref} open={open} onClose={() => toggle(false)} align="bottom-right">
        <div className="w-56 py-1" role="none">
          <button type="button" role="menuitem" data-testid={`context-msg-copy-${m.id}`} onClick={() => { toggle(false); copy(); }}
            className={`${MENU_ITEM} text-gray-800 hover:bg-gray-50`}><Copy className="h-4 w-4 text-gray-500" aria-hidden />Copy text</button>
          {/* KEEP AS KNOWLEDGE (KNOWLEDGE.md §14) — the network's owner reviews it (Settings → Knowledge) */}
          {/* never an answer still waiting for its person (a stand-in draft) — keep what was confirmed */}
          {typeof onKeep === 'function' && m.text && m.pending?.status !== 'pending' ? keepKinds.map((k) => (
            <button key={k} type="button" role="menuitem" data-testid={`context-msg-keep-${k}-${m.id}`}
              onClick={() => { toggle(false); onKeep(m, k); }}
              className={`${MENU_ITEM} text-gray-800 hover:bg-gray-50`}><BookmarkPlus className="h-4 w-4 text-gray-500" aria-hidden />{KEEP_LABEL[k] || k}</button>
          )) : null}
          {editable ? (
            <button type="button" role="menuitem" data-testid={`context-msg-edit-${m.id}`} onClick={() => { toggle(false); onStartEdit(); }}
              className={`${MENU_ITEM} text-gray-800 hover:bg-gray-50`}><Pencil className="h-4 w-4 text-gray-500" aria-hidden />{m.standin ? 'Correct this answer' : 'Edit message'}</button>
          ) : null}
          {deletable ? (<>
            <div className="my-1 h-px bg-gray-100" role="separator" />
            <button type="button" role="menuitem" data-testid={`context-msg-delete-${m.id}`} onClick={() => { toggle(false); onDelete(m); }}
              className={`${MENU_ITEM} text-rose-600 hover:bg-rose-50`}><Trash2 className="h-4 w-4" aria-hidden />Delete message</button>
          </>) : null}
        </div>
      </PortalMenu>
    </div>
  );
}

/** Inline editor (Slack): Enter saves · Shift+Enter new line · Esc cancels; an unchanged/empty text doesn't save. */
function MessageEditor({ m, onSave, onCancel }) {
  const [text, setText] = useState(m.text || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const box = useRef(null);
  useEffect(() => { const b = box.current; if (b) { b.focus(); b.setSelectionRange(b.value.length, b.value.length); } }, []);
  const save = async () => {
    const t = text.trim();
    if (!t || t === (m.text || '').trim()) { onCancel(); return; }
    setBusy(true); setErr('');
    try { await onSave(m, t); onCancel(); } catch (e) { setErr((e && e.message) || "Couldn't save — try again."); setBusy(false); }
  };
  return (
    <div data-testid={`context-msg-editor-${m.id}`} className="mt-1 rounded-lg border border-gray-300 bg-white p-2 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300">
      <textarea ref={box} value={text} rows={Math.min(8, Math.max(2, text.split('\n').length))} disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void save(); }
        }}
        className={`w-full resize-none border-0 p-0 ${MSG_TEXT} text-gray-900 outline-none focus:ring-0`} />
      <div className="mt-1 flex items-center justify-end gap-1.5">
        <span className="mr-auto text-[0.75rem] text-gray-500">{err ? <span className="text-rose-600">{err}</span> : 'Enter to save · Esc to cancel'}</span>
        <button type="button" onClick={onCancel} disabled={busy}
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="button" data-testid={`context-msg-save-${m.id}`} onClick={() => void save()} disabled={busy}
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">Save</button>
      </div>
    </div>
  );
}

/** One message in the stream — a person, or the agent they asked (its reply). Slack-like row: hover shades it and
 *  pops the action bar; ``canEdit`` / ``canDelete`` decide what the bar offers (the server is the real gate); a
 *  deleted message renders as a tombstone. */
/** "2:37" — the hover time in a continuation's gutter (Slack): the shared clock format, without AM/PM. */
const formatMessageTimeShort = (at) => String(formatClockTime(at) || '').replace(/\s?[AP]M$/i, '');

/** ASK PROGRESS (MODES.md §10.2a) — an agent answering, LIVE for everyone in the room: its avatar pulses, its phase
 *  shimmers ("Reading the conversation…" → "Thinking…"), then the answer types itself out with a caret. Replaced in
 *  place by the stored answer when it lands. ``onGrow`` keeps a reader at the bottom pinned as the text grows. */
export function AgentAnswering({ p, onGrow = null, askerName = '' }) {
  const text = useSmoothText(p && p.text);
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!p) return undefined;
    const tick = () => setSecs(Math.max(0, Math.floor((Date.now() - (p.since || Date.now())) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [p && p.since]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (typeof onGrow === 'function') onGrow(); }, [text.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const writing = !!text;
  const label = writing ? '' : (p.label || (p.phase === 'reading' ? 'Reading the conversation'
    : p.phase === 'thinking' ? 'Thinking' : 'Getting started'));
  return (
    <div data-testid={`agent-answering-${p.key}`} data-phase={writing ? 'writing' : p.phase} aria-live="polite"
      aria-busy="true" className="sf-rise -mx-3 flex gap-2 rounded-md px-3 pb-0.5 pt-1.5">
      <span className={writing ? '' : 'sf-ring'}>
        <PersonAvatar size="md" agent className="mt-0.5" person={{ name: p.agentName, sub: p.agentId }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 leading-[1.46668]">
          <span className="text-[0.9375rem] font-bold text-indigo-700">{p.agentName}</span>
          <span className="rounded bg-indigo-50 px-1 text-[0.6875rem] text-indigo-600">agent</span>
          {askerName ? <span className="text-[0.8125rem] text-gray-500">answering {askerName}</span> : null}
          {secs >= 3 ? <span className="text-[0.75rem] tabular-nums text-gray-400">{secs}s</span> : null}
        </div>
        {writing ? (
          <div className={`${MSG_TEXT} text-gray-900`} data-testid={`agent-answering-text-${p.key}`}>
            {/* what it looked up stays in view, folded to one line, while the answer types */}
            {p.web ? <WebProgress web={p.web} compact testId={`agent-answering-web-${p.key}`} /> : null}
            <MarkdownRenderer content={text} size="chat" />
            <span className="sf-caret" aria-hidden />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 py-0.5" data-testid={`agent-answering-phase-${p.key}`}>
              <span className="flex gap-1" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400"
                    style={{ animationDelay: `${i * 140}ms`, animationDuration: '1s' }} />
                ))}
              </span>
              <span className="sf-shimmer-text text-[0.875rem] font-medium">{label}…</span>
            </div>
            {/* THE LIVE WEB (MODES.md §10.9): every search + page as it happens, for everyone in the room */}
            {p.web ? <WebProgress web={p.web} testId={`agent-answering-web-${p.key}`} /> : null}
          </>
        )}
      </div>
    </div>
  );
}

export function DiscussionMessage({ m, onAdopt = null, me = '', timeOnly = false, canEdit = false, canDelete = false,
  onEdit = null, onDelete = null, onSignal = null, nameOf = null, onReplyInThread = null, isReply = false, personOf = null,
  renderFiles = null, onConfirm = null, compact = false, questionOf = null, onJumpTo = null, renderLinks = null,
  onKeep = null, keepKinds = [] }) {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  if (!m) return null;
  if (m.deleted) {
    return (
      <div data-testid={`context-msg-${m.id}`} data-kind="deleted" className="flex items-center py-0.5 pl-11 text-[0.8125rem] italic text-gray-400">
        This message was deleted.
      </div>
    );
  }
  if (m.kind === 'system') {
    // A ROSTER LINE (ROOMS.md §roster-lines, Slack): "Ana Lima joined the room — added by Raj"; consecutive joins roll
    // up into one ("Ana, Raj and 2 others joined"). Never context.
    const sys = m.system || {};
    const names = (m.rollup || [m]).map((x) => (x.system || {}).subjectName || 'Someone');
    const many = names.length > 1;
    const who = many ? (names.length > 3 ? `${names.slice(0, 2).join(', ')} and ${names.length - 2} others` :
      `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`) : names[0];
    const by = sys.actorName || '';
    const line = {
      added: <>{many ? 'joined the room' : 'joined the room'}{by ? <> — added by <b className="font-semibold text-gray-700">{by}</b></> : null}</>,
      rejoined: <>rejoined the room{by ? <> — added by <b className="font-semibold text-gray-700">{by}</b></> : null}</>,
      left: <>left the room</>,
      removed: <>was removed{by ? <> by <b className="font-semibold text-gray-700">{by}</b></> : null}</>,
      elevated: <>joined as owner (workspace owner)</>,
    }[sys.event];
    return (
      <div data-testid={`context-msg-${m.id}`} data-kind="system" data-event={sys.event || ''}
        className="flex items-center gap-1.5 py-0.5 pl-11 text-[0.8125rem] text-gray-500">
        {sys.event === 'role' ? (
          <span><b className="font-semibold text-gray-700">{by || 'Someone'}</b> made <b className="font-semibold text-gray-700">{who}</b>{' '}
            {(sys.roles || []).map((r) => r.charAt(0) + r.slice(1).toLowerCase()).join(', ') || 'a member'}</span>
        ) : (
          <span><b className="font-semibold text-gray-700">{who}</b> {line || m.text}</span>
        )}
        <time dateTime={m.at || undefined} title={formatMessageTimeFull(m.at)} className="text-[0.75rem] text-gray-400">{_time(m.at, timeOnly)}</time>
      </div>
    );
  }
  if (m.kind === 'adopt') {
    // an ACTIVITY line (MODES.md §10.4): a person made an agent's answer the team's decision
    return (
      <div data-testid={`context-msg-${m.id}`} data-kind="adopt" className="flex items-center gap-1.5 py-0.5 pl-11 text-[0.8125rem] text-gray-500">
        <span className="text-emerald-600" aria-hidden>✓</span>
        <span><span className="font-semibold text-gray-700">{authorLabel(m, personOf, 'A teammate')}</span> adopted{' '}
          {m.agentName || m.agentId || 'the agent'}&apos;s suggestion as the team&apos;s decision</span>
        <time dateTime={m.at || undefined} title={formatMessageTimeFull(m.at)} className="text-[0.75rem] text-gray-400">{_time(m.at, timeOnly)}</time>
      </div>
    );
  }
  const agent = m.kind === 'agent';
  // a person's name: the one stored with the message, else the room's people directory (a row stored WITHOUT a name
  // — a token without claims — must still show who wrote it, L151); "Teammate" only when nobody knows
  const known = !agent && personOf ? personOf(m.authorSub) : null;
  const who = agent ? (m.agentName || m.agentId) : authorLabel(m, personOf, 'Teammate');
  // this message TAGGED me (MENTIONS.md) → highlighted, for me only
  const mentionsMe = !!me && Array.isArray(m.mentions) && m.mentions.includes(me);
  const editable = canEdit && !agent && typeof onEdit === 'function';
  const deletable = canDelete && typeof onDelete === 'function';
  const shade = editing ? 'bg-amber-50/70' : mentionsMe ? 'bg-amber-50 ring-1 ring-amber-200' : menuOpen ? 'bg-gray-50' : 'hover:bg-gray-50 focus-within:bg-gray-50';
  return (
    <div data-testid={`context-msg-${m.id}`} data-kind={m.kind} data-mentions-me={mentionsMe ? 'true' : undefined}
      data-compact={compact ? 'true' : undefined}
      className={`group relative -mx-3 flex gap-2 rounded-md px-3 transition-colors ${compact ? 'py-0.5' : 'pb-0.5 pt-1.5'} ${shade}`}>
      {/* SLACK GROUPING (MODES.md §3f): a continuation has no avatar / name — its time shows here on hover */}
      {compact ? (
        <span aria-hidden data-testid={`context-msg-gutter-${m.id}`}
          className="w-9 shrink-0 pt-[0.2rem] text-right text-[0.6875rem] tabular-nums leading-[1.46668] text-gray-400 opacity-0 group-hover:opacity-100">
          {formatMessageTimeShort(m.at)}
        </span>
      ) : (
        <PersonAvatar size="md" agent={agent} className="mt-0.5" testId={`context-msg-avatar-${m.id}`}
          person={agent ? { name: who, sub: m.agentId } : ((personOf && personOf(m.authorSub)) || { name: who, sub: m.authorSub })} />
      )}
      <div className="min-w-0 flex-1">
        <div className={`${compact ? 'sr-only' : 'flex flex-wrap items-baseline gap-x-2 leading-[1.46668]'}`}>
          <span className={`text-[0.9375rem] font-bold ${agent ? 'text-indigo-700' : 'text-gray-900'}`}>{who}</span>
          {agent ? <span className="rounded bg-indigo-50 px-1 text-[0.6875rem] text-indigo-600">agent</span> : null}
          {/* THE ANSWER NAMES ITS QUESTION (MODES.md §10.2b) — the mirror of the question's "asked <agent>":
              "answered <person>", a link back to the question (scrolls to it + flashes it) */}
          {agent && m.replyTo ? ((q) => {
            const asker = q ? authorLabel(q, personOf, 'a teammate') : 'a question';
            return typeof onJumpTo === 'function' && q ? (
              <button type="button" data-testid={`context-msg-answered-${m.id}`} onClick={() => onJumpTo(q.id)}
                title={`Go to the question: “${String(q.text || '').slice(0, 120)}”`}
                className="text-[0.8125rem] text-gray-500 hover:text-indigo-700 hover:underline">
                answered {asker}
              </button>
            ) : <span data-testid={`context-msg-answered-${m.id}`} className="text-[0.8125rem] text-gray-500">answered {asker}</span>;
          })(typeof questionOf === 'function' ? questionOf(m.replyTo) : null) : null}
          {/* STAND-IN (STAND-IN.md): ALWAYS labelled — the agent answered FOR this person, never as them */}
          {agent && m.standin ? (
            <span data-testid={`context-msg-standin-${m.id}`} title={m.standin.basis || ''}
              className="inline-flex items-center gap-1 rounded bg-amber-50 px-1 text-[0.6875rem] text-amber-800 ring-1 ring-amber-200">
              on behalf of {m.standin.forName || 'a teammate'}
              {m.standin.confirmed ? <span className="text-emerald-700">· sent by them</span>
                : <span className="tabular-nums">· {Math.round(Number(m.standin.confidence || 0) * 100)}%</span>}
              {m.standin.correctedAt ? <span className="text-gray-500">· corrected</span> : null}
            </span>
          ) : null}
          {/* PENDING CONFIRMATION (STAND-IN.md §9): the room sees it as TENTATIVE until its supervisor confirms */}
          {agent && m.pending && m.pending.status === 'pending' ? (
            <span data-testid={`context-msg-pending-${m.id}`} title={m.pending.basis || 'Not confirmed yet'}
              className="rounded bg-gray-100 px-1 text-[0.6875rem] font-medium text-gray-600 ring-1 ring-gray-200">
              ⏳ Pending {(m.pending.for || []).map((p) => p.name).filter(Boolean).join(' or ') || 'its supervisor'}&apos;s confirmation
              <span className="tabular-nums"> · {Math.round(Number(m.pending.confidence || 0) * 100)}%</span>
            </span>
          ) : agent && m.pending && m.pending.status === 'confirmed' ? (
            <span data-testid={`context-msg-confirmed-${m.id}`} className="rounded bg-emerald-50 px-1 text-[0.6875rem] text-emerald-700">
              ✓ {m.pending.decision === 'modify' ? 'corrected' : 'confirmed'} by {m.pending.confirmedBy || 'its supervisor'}
            </span>
          ) : null}
          {/* the tier it answered on (MODES.md §3e) — "· Reason" */}
          {agent && m.modelTier ? (
            <span data-testid={`context-msg-model-${m.id}`} title="The model tier this answer used"
              className="text-[0.6875rem] text-gray-500">· {contextModelLabel(m.modelTier)}</span>
          ) : null}
          {!agent && m.agentId ? <span className="text-[0.8125rem] text-gray-500">asked {m.agentName || m.agentId}</span> : null}
          {mentionsMe ? <span className="rounded bg-amber-100 px-1 text-[0.6875rem] font-medium text-amber-800">mentioned you</span> : null}
          <time dateTime={m.at || undefined} title={formatMessageTimeFull(m.at)} className="text-[0.75rem] text-gray-500">{_time(m.at, timeOnly)}</time>
          {agent && m.adoptedBy ? (
            <span data-testid={`context-msg-adopted-${m.id}`} className="rounded bg-emerald-50 px-1 text-[0.6875rem] text-emerald-700"
              title="A person made this the team's decision — the context records it as decided">
              ✓ adopted by {m.adoptedBy}
            </span>
          ) : null}
          {agent && !m.adoptedBy && typeof onAdopt === 'function' ? (
            <button type="button" data-testid={`context-msg-adopt-${m.id}`} onClick={() => onAdopt(m)}
              title="Make this the team's decision — until then the context keeps it as the agent's suggestion"
              className="rounded px-1 text-[0.75rem] text-gray-500 opacity-0 transition-opacity hover:bg-emerald-50 hover:text-emerald-700 focus:opacity-100 group-hover:opacity-100">
              Adopt
            </button>
          ) : null}
        </div>
        {editing ? (
          <MessageEditor m={m} onSave={editing === 'confirm' ? ((mm, t) => onConfirm(mm, 'modify', t)) : onEdit}
            onCancel={() => setEditing(false)} />
        ) : (
          <div className={`${MSG_TEXT} ${m.pending && m.pending.status === 'pending' ? 'text-gray-500' : 'text-gray-900'}`}
            data-pending={m.pending && m.pending.status === 'pending' ? 'true' : undefined}>
            {/* a person's message is Markdown too (MODES.md §3c — bold / italic / lists from the composer), line
                breaks kept exactly as typed */}
            <LongMessage id={m.id}>
              {agent ? <MarkdownRenderer content={linkCitations(m.text, m.sources)} size="chat" />
                : <div data-testid={`context-msg-text-${m.id}`} className="break-words"><MarkdownRenderer content={keepLineBreaks(m.text)} size="chat" /></div>}
            </LongMessage>
            {/* THE LIVE WEB (MODES.md §10.9) — the pages this answer stands on; [n] in the text links to them */}
            {agent && (m.sources || []).length ? <SourceCards sources={m.sources} testId={`web-sources-${m.id}`} /> : null}
            {m.editedAt ? (
              <span data-testid={`context-msg-edited-${m.id}`} className="ml-1 text-[0.75rem] text-gray-500"
                title={`Edited ${formatMessageTimeFull(m.editedAt)}`}>(edited)</span>
            ) : null}
          </div>
        )}
        {/* ONLY the people it waits for confirm it (the server enforces it) — Approve · Modify · Discard */}
        {agent && !editing && m.pending && m.pending.status === 'pending' && typeof onConfirm === 'function'
          && (m.pending.for || []).some((p) => p.sub && p.sub === me) ? (
            <div data-testid={`context-msg-confirm-${m.id}`} className="mt-1 flex items-center gap-1.5">
              <span className="text-[0.75rem] text-gray-500">{m.pending.kind === 'standin' ? 'Your stand-in answered for you —' : 'Waiting for you —'}</span>
              <button type="button" data-testid={`context-msg-approve-${m.id}`} onClick={() => onConfirm(m, 'approve')}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700">Approve</button>
              <button type="button" data-testid={`context-msg-modify-${m.id}`} onClick={() => setEditing('confirm')}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">Modify</button>
              <button type="button" data-testid={`context-msg-discard-${m.id}`} onClick={() => onConfirm(m, 'discard')}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100">Discard</button>
            </div>
          ) : null}
        {/* LINK PREVIEWS (CONTEXT-DOCS.md §L) — the host renders the cards (it knows the room) */}
        {typeof renderLinks === 'function' && (m.links || []).length ? renderLinks(m) : null}
        {/* FILE CARDS (CONTEXT-DOCS.md §P1) when the host can open them; else the names (other surfaces) */}
        {typeof renderFiles === 'function' && (m.attachments || []).length ? (
          <div data-testid={`context-msg-files-${m.id}`} className="mt-1.5 flex flex-wrap gap-2">{renderFiles(m)}</div>
        ) : (m.files || []).length ? (
          <div data-testid={`context-msg-files-${m.id}`} className="mt-0.5 text-[0.8125rem] text-gray-500">📎 {m.files.join(', ')}</div>
        ) : null}
        <SignalPills m={m} me={me} nameOf={nameOf} onSignal={onSignal} />
      </div>
      {editing ? null : (
        <MessageActions m={m} me={me} editable={editable} deletable={deletable} onStartEdit={() => setEditing(true)}
          onDelete={onDelete} onMenuOpen={setMenuOpen} onSignal={onSignal}
          onReplyInThread={isReply ? null : onReplyInThread} onKeep={onKeep} keepKinds={keepKinds} />
      )}
    </div>
  );
}

/** THE REPLY BAR (Slack) under a root in the room stream: the repliers' initials, "4 replies" (a link), "Last reply …"
 *  and a › — the replies themselves live in the THREAD PANE (MODES.md §3b), never inline. */
export function ReplyBar({ root, nameOf = null, personOf = null, onOpen, active = false }) {
  const count = Number(root.replyCount) || 0;
  if (!count || root.deleted) return null;
  const subs = (root.replySubs || []).slice(0, 5);
  return (
    <div className="pl-11">
      <button type="button" data-testid={`reply-bar-${root.id}`} onClick={() => onOpen(root)} aria-expanded={active}
        className={`group/rb -ml-1 mt-0.5 flex w-full max-w-md items-center gap-1.5 rounded-md border px-1 py-0.5 text-left text-[0.8125rem] transition-colors ${
          active ? 'border-gray-200 bg-white' : 'border-transparent hover:border-gray-200 hover:bg-white'}`}>
        <span className="flex gap-0.5" aria-hidden>
          {subs.map((sub) => (
            <PersonAvatar key={sub} size="sm" testId={`reply-bar-avatar-${sub}`}
              person={(personOf && personOf(sub)) || { sub, name: (nameOf && nameOf(sub)) || '' }} />
          ))}
        </span>
        <span data-testid={`reply-count-${root.id}`} className="font-bold text-indigo-700 group-hover/rb:underline">
          {count} {count === 1 ? 'reply' : 'replies'}
        </span>
        {root.lastReplyAt ? (
          <span className="truncate text-gray-500">
            <span className="group-hover/rb:hidden">Last reply {formatMessageTime(root.lastReplyAt)}</span>
            <span className="hidden group-hover/rb:inline">View thread</span>
          </span>
        ) : null}
        <ChevronRight className="ml-auto h-[1.125rem] w-[1.125rem] text-gray-400 opacity-0 group-hover/rb:opacity-100" aria-hidden />
      </button>
    </div>
  );
}

/** An empty room in Context: how to start (in place of "No activity yet"). */
export function ContextStartHint() {
  return (
    <div data-testid="context-start-hint" className="flex items-center justify-center py-12">
      <span className="inline-flex max-w-md items-center rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-center text-sm text-gray-500 shadow-sm">
        Start the conversation — what is the project, what do you already know, what's unclear? Pick an agent to ask it
        directly.
      </span>
    </div>
  );
}

/** DAY-GROUP DIVIDER (Slack-style) — the group label ("Today", "Yesterday", "Last week", …) with a line fading out
 *  beneath it. */
export function StreamDayDivider({ label, dayKey = '', days = [], onJump = null }) {
  // SLACK DAY DIVIDER: a full-width line with the day as a centred PILL that STICKS to the top while you scroll that
  // day — it is the first child of that day's SECTION (``streamDaySections``), so it lets go when the day scrolls
  // out and only ONE pill is ever on top (they used to stack, one parent for all); the pill opens "Jump to…".
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const jump = (k) => { setOpen(false); if (onJump) onJump(k); };
  return (
    <>
      <div role="separator" aria-label={label} data-testid="stream-day-divider" data-day={dayKey || label}
        className="mt-4 h-px bg-gray-200" />
      <div className="pointer-events-none sticky top-1 z-10 -mt-[0.8rem] mb-1.5 flex justify-center">
        <button ref={ref} type="button" data-testid="stream-day-pill" aria-label="Jump to date" aria-haspopup="menu"
          aria-expanded={open} onClick={() => setOpen((o) => !o)} disabled={typeof onJump !== 'function'}
          className="pointer-events-auto inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[0.8125rem] font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-white">
          {label}
          {typeof onJump === 'function' ? <ChevronDown className="h-3.5 w-3.5 text-gray-500" aria-hidden /> : null}
        </button>
      </div>
      {typeof onJump === 'function' ? (
        <PortalMenu anchorRef={ref} open={open} onClose={() => setOpen(false)} align="bottom-left">
          <div className="w-56 py-1 text-[0.8125rem]" role="menu" data-testid="stream-day-menu">
            <div className="px-3 pb-1 pt-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-gray-400">Jump to…</div>
            <button type="button" role="menuitem" onClick={() => jump('latest')} data-testid="stream-day-jump-latest"
              className="block w-full px-3 py-1.5 text-left text-gray-800 hover:bg-gray-50">Most recent</button>
            {(days || []).map((d) => (
              <button key={d.key} type="button" role="menuitem" onClick={() => jump(d.key)} data-testid={`stream-day-jump-${d.key}`}
                className={`block w-full px-3 py-1.5 text-left hover:bg-gray-50 ${d.key === dayKey ? 'font-semibold text-indigo-700' : 'text-gray-800'}`}>{d.label}</button>
            ))}
            <button type="button" role="menuitem" onClick={() => jump('earliest')} data-testid="stream-day-jump-earliest"
              className="block w-full border-t border-gray-100 px-3 py-1.5 text-left text-gray-800 hover:bg-gray-50">The very beginning</button>
          </div>
        </PortalMenu>
      ) : null}
    </>
  );
}

/** "N new items · 👍2 ❓1 · Build context" — only when there's something to build (or a build runs / signals exist).
 *  ``inline`` = sits in the composer's UPPER DECK, RIGHT-aligned beside the mode switcher. There the count, the
 *  readiness (icon + number only — the words live in the tooltip) and the Build button are ONE joined chip, so it
 *  reads as "this button builds THESE items, and here is where the team stands". */
/** "450" · "3.2k" · "12k" — a rough size, never more precise than it is. Pure. */
export function formatTokens(n = 0) {
  const v = Math.max(0, Number(n) || 0);
  if (v < 1000) return String(Math.round(v));
  return v < 10000 ? `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k` : `${Math.round(v / 1000)}k`;
}

export function ContextBuildBar({ build = null, onBuild = null, pendingAgent = '', inline = false }) {
  const pend = (build && build.pending) || { messages: 0, files: 0 };
  const building = !!build && build.status === 'building';
  const _anyReady = ['agreed', 'neutral', 'excluded', 'disputed', 'unclear'].some((k) => Number(((build && build.readiness) || {})[k]) > 0);
  if (!pendingAgent && !building && !(pend.messages > 0) && !(build && build.lastError) && !_anyReady
    && !(Number(pend.reading) > 0)) return null;
  const count = `${pend.messages} new item${pend.messages === 1 ? '' : 's'}`;
  const countTitle = `${count} not yet in the context`;
  const canBuild = typeof onBuild === 'function' && pend.messages > 0 && !building;
  // CONTEXT READINESS (MODES.md §3a) — the POINTS the next build folds, each ONCE by its standing (one vote per
  // person; the most-voted signal wins, ties toward caution): agreed → built first · unclear → built last (open
  // question) · excluded (voted down) → left out. Points, not people — the chips under a message count people.
  const rd = (build && build.readiness) || {};
  const _excl = Number(rd.excluded ?? rd.disputed ?? 0);
  // icon + COUNT only (the tooltip says what each means) — every standing, neutral included, so the bar reads the
  // whole next build at a glance and moves LIVE as the team votes
  const ready = [['agreed', 'agreed — built first', ThumbsUp, 'text-emerald-700', Number(rd.agreed || 0)],
    ['neutral', 'not voted on — built next', Circle, 'text-gray-500', Number(rd.neutral || 0)],
    ['unclear', 'unclear — built last, as open questions', HelpCircle, 'text-amber-700', Number(rd.unclear || 0)],
    ['excluded', 'voted down — left out', ThumbsDown, 'text-rose-700', _excl]].filter((r) => r[4] > 0);
  // ~HOW BIG the next build is, against when it builds itself (MODES.md §10.7) — "time to build, or adjust?"
  const sz = (build && build.size) || null;
  const _tok = sz ? Number(sz.tokens || 0) : 0;
  const _fill = sz ? Math.max(_tok / Math.max(1, Number(sz.autoTokens || 8000)),
    Number(pend.messages || 0) / Math.max(1, Number(sz.autoMessages || 20))) : 0;
  const sizeChip = sz && _tok > 0 ? (
    <span data-testid="context-build-size" data-fill={_fill >= 0.9 ? 'full' : _fill >= 0.5 ? 'half' : 'low'}
      title={`About ${_tok.toLocaleString()} tokens of new material — the points the next build reads (voted-down `
        + `ones left out). The context builds itself at about ${Math.round(Number(sz.autoTokens || 8000) / 1000)}k tokens `
        + `or ${Number(sz.autoMessages || 20)} messages — build now, or adjust the votes first.`}
      className={`whitespace-nowrap tabular-nums ${inline ? 'border-l border-gray-200 bg-white px-2 py-0.5' : ''} ${
        _fill >= 0.9 ? 'font-medium text-indigo-700' : _fill >= 0.5 ? 'text-amber-700' : 'text-gray-500'}`}>
      ~{formatTokens(_tok)} tokens
    </span>
  ) : null;
  const readiness = ready.length ? (
    <span data-testid="context-readiness"
      title={`Of the new points: ${ready.map(([, label, , , n]) => `${n} ${n === 1 ? 'point' : 'points'} ${label}`).join(' · ')}`}
      className={`inline-flex items-center gap-2 ${inline ? 'border-l border-gray-200 bg-white px-2 py-0.5' : ''}`}>
      {ready.map(([k, label, Icon, tone, n]) => (
        <span key={k} data-testid={`context-readiness-${k}`} aria-label={`${n} ${n === 1 ? 'point' : 'points'} ${label}`}
          className={`inline-flex items-center gap-0.5 font-medium tabular-nums ${tone}`}>
          <Icon className="h-3 w-3" aria-hidden />{n}
        </span>
      ))}
    </span>
  ) : null;
  const button = canBuild ? (
    <button type="button" data-testid="context-build-button" onClick={onBuild}
      title="Fold what's new into the project's context (the refined version Plan works from)"
      className={inline
        ? 'border-l border-amber-200 bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white transition-colors hover:bg-indigo-700'
        : 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700'}>
      Build context
    </button>
  ) : null;
  return (
    <div data-testid="context-build-status"
      className={inline ? 'ml-auto flex min-w-0 items-center justify-end gap-1.5 whitespace-nowrap text-[11px] text-gray-500' : 'mx-6 mb-1 flex items-center gap-2 text-[11px] text-gray-500'}>
      {pendingAgent ? <span data-testid="context-discussion-pending" className="italic text-indigo-500">{pendingAgent} is answering…</span> : null}
      {inline ? (
        building ? (
          <span data-testid="context-build-group" className="inline-flex items-stretch overflow-hidden rounded-md ring-1 ring-indigo-200">
            <span className="bg-indigo-50 px-2 py-0.5 text-indigo-700">Building the context…</span>
            {readiness}
          </span>
        ) : (pend.messages > 0 || readiness) ? (
          <span data-testid="context-build-group" className="inline-flex items-stretch overflow-hidden rounded-md ring-1 ring-amber-200">
            {pend.messages > 0 ? <span title={countTitle} className="bg-amber-50 px-2 py-0.5 text-amber-800">{count}</span> : null}
            {readiness}
            {sizeChip}
            {button}
          </span>
        ) : null
      ) : (<>
        {building ? <span className="text-indigo-600">Building the context…</span>
          : pend.messages > 0 ? <span title={countTitle} className="text-amber-700">{count}</span> : null}
        {readiness}
        {sizeChip}
        {button}
      </>)}
      {/* documents still being read — the build can go now; they join a later one (CONTEXT-DOCS §P3) */}
      {Number(pend.reading) > 0 && !building ? (
        <span data-testid="context-build-reading" className="text-[11px] text-gray-400">
          {pend.reading} document{Number(pend.reading) === 1 ? '' : 's'} still being read
        </span>
      ) : null}
      {build && build.lastError && !building ? <span data-testid="context-build-error" className="text-rose-600">{build.lastError}</span> : null}
    </div>
  );
}
